import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Outlet, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CsrAPI } from "../apis/CsrAPI";
import { syncStudyDataWithDocuments } from "../store/singletons/csrDataPollingSingleton";
import {
  fetchScsTflContent,
  resetScsForSummary,
} from "../store/slices/scsDataSlice";
import {
  CSR_STATUS,
  FILE_UPLOAD_STATUS,
  DOCUMENT_TYPE,
  SCS_DOCUMENT_TYPE,
  FILES_STATUS,
  LS_STATUS,
  PN_DOCUMENT_TYPE,
} from "../utils/Constants";
import FormatDate from "../utils/FormatDate";
import { saveAs } from "file-saver";
import { usePermissions } from "./PermissionsContext";
import { PERMISSIONS } from "../utils/permissions";
export const CsrContext = createContext(null);

let updateFilesStatus;
let updateScsFilesStatus;

export default function CsrProvider() {
  const { studyId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { hasPermission, permissionsLoading } = usePermissions();

  const statusPollingRef = useRef(null);
  const errRef = useRef();

  const [ajaxLoading, setAjaxLoading] = useState(false);
  const [studyInfoLoading, setStudyInfoLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [currentStudyName, setCurrentStudyName] = useState("");
  const [currentLaySummaryName, setCurrentLaySummaryName] = useState("");

  // Name of the record the current page belongs to, shown in the dashboard
  // header on every page of a journey. SCS keeps its own copy in the scsData
  // slice; CSR, LS and PN are resolved here so a refresh or a deep link into
  // any wizard page still has a name to show. Always cleared first when the
  // product or the id changes, so one record's name never shows on another's.
  const [headerStudyName, setHeaderStudyName] = useState("");

  // "csr" | "scs" | "pn" | "ls" | "dashboard" | ... — the product segment of
  // the current URL. Stable while navigating within a journey, unlike the
  // full pathname, so effects keyed on it don't re-run page by page.
  const app = location.pathname.split("/")[1];

  const [currentRequestId, setCurrentRequestId] = useState("");
  const [mainStatus, setMainStatus] = useState(CSR_STATUS.inProgress);
  const [currentRequestTime, setCurrentRequestTime] = useState("");

  const [currentRequestIdOfLaySummary, setCurrentRequestIdOfLaySummary] =
    useState("");
  const [mainStatusOfLaySummary, setMainStatusOfLaySummary] = useState(
    CSR_STATUS.inProgress
  );
  const [currentRequestTimeOfLaySummary, setCurrentRequestTimeOfLaySummary] =
    useState("");

  const [currentTrafficLights, setCurrentTrafficLights] = useState([]);

  const cspProgressRef = useRef(null);
  const sapProgressRef = useRef(null);
  const tflProgressRef = useRef(null);
  const listingProgressRef = useRef(null);
  const ciomsProgressRef = useRef(null);
  const [cspProgress, setCspProgress] = useState({ val: [] });
  const [sapProgress, setSapProgress] = useState({ val: [] });
  const [tflProgress, setTflProgress] = useState({ val: [] });
  const [listingProgress, setListingProgress] = useState({ val: [] });
  const [ciomsProgress, setCiomsProgress] = useState({ val: [] });

  const [documentsList, setDocumentsList] = useState({
    csp: [],
    sap: [],
    tfl: [],
    listing: [],
    cioms: [],
  });

  const [filesStatus, setFilesStatus] = useState(FILES_STATUS.ready);
  const [questionsStatus, setQuestionsStatus] = useState(true);
  const [selectedStudy, setSelectedStudy] = useState("");

  const gidsProgressRef = useRef(null);
  const sdapProgressRef = useRef(null);
  const csrsProgressRef = useRef(null);
  const isoProgressRef = useRef(null);
  const [gidsProgress, setGidsProgress] = useState({ val: [] });
  const [sdapProgress, setSdapProgress] = useState({ val: [] });
  const [csrsProgress, setCsrsProgress] = useState({ val: [] });
  const [isoProgress, setIsoProgress] = useState({ val: [] });

  const [scsDocumentsList, setScsDocumentsList] = useState({
    gids: [],
    sdap: [],
    csrs: [],
    iso: [],
  });
  const [scsFilesStatus, setScsFilesStatus] = useState(FILES_STATUS.ready);

  const stopStatusPolling = useCallback(() => {
    if (statusPollingRef.current) {
      clearTimeout(statusPollingRef.current);
      statusPollingRef.current = null;
    }
  }, []);

  const getStudyInfo = useCallback(studyId => {
    // "new" is a placeholder id used before a study exists, so there is
    // nothing to wait for on the server.
    if (studyId && studyId !== "new") setStudyInfoLoading(true);

    CsrAPI.getOneStudy({ studyId })
      .then(studyInfo => {
        setCurrentStudyName(studyInfo.name);
        // Same lookup the CSR header name needs — reuse it rather than
        // repeating GET /studies/{id} from the header effect below.
        setHeaderStudyName(studyInfo.name || "");

        if (studyInfo.latest_request_status === "render available") {
          setCurrentRequestId(studyInfo.latest_request_id);
          setMainStatus(CSR_STATUS.ready);
        }

        if (studyInfo.latest_request_id)
          getCsrStatus(studyInfo.study_id, studyInfo.latest_request_id);
      })
      .catch(() => {
        setErrMsg("No Server Response");
      })
      .finally(() => {
        setStudyInfoLoading(false);
      });
  }, []);

  const getLaySummaryInfo = useCallback(lay_summary_id => {
    CsrAPI.getLatestLaySummaryRequest({ lay_summary_id })
      .then(studyInfo => {
        setCurrentRequestIdOfLaySummary(studyInfo.request.request_id);
        setCurrentRequestTimeOfLaySummary(
          FormatDate(studyInfo.request.created_at)
        );
        setMainStatusOfLaySummary(studyInfo.request.request_status);

        if (studyInfo.request.request_id)
          getLaySummaryStatus(
            studyInfo.request.lay_summary_id,
            studyInfo.request.request_id
          );
      })
      .catch(error => {
        if (error.response?.status === 404) {
          setMainStatusOfLaySummary(LS_STATUS.readyToStart);
        } else {
          // setErrMsg("Request details not found!");//TODO - BE needs to handle 404 for request id not found
        }
      });
  }, []);

  const getLaySummaryStatus = useCallback(
    (lay_summary_id, requestId) => {
      stopStatusPolling();
      CsrAPI.getLaySummaryStatus({ lay_summary_id, requestId })
        .then(result => {
          switch (result.request_status) {
            case "PENDING":
              setMainStatusOfLaySummary(LS_STATUS.generating);
              setAjaxLoading(true);
              statusPollingRef.current = setTimeout(() => {
                getLaySummaryStatus(lay_summary_id, requestId);
              }, 30000);
              break;
            case "AI_SUMMARY_COMPLETED":
              setMainStatusOfLaySummary(LS_STATUS.aiSummaryReady);
              setAjaxLoading(true);
              setErrMsg("");
              statusPollingRef.current = setTimeout(() => {
                getLaySummaryStatus(lay_summary_id, requestId);
              }, 30000);
              break;
            case "COMPLETED":
              setMainStatusOfLaySummary(LS_STATUS.ready);
              setAjaxLoading(false);
              setErrMsg("");
              break;
            case "COMPLETED_WITH_ERRORS":
              setMainStatusOfLaySummary(LS_STATUS.errored);
              setAjaxLoading(false);
              break;
            default:
              console.warn(`Unexpected CSR status: ${result.status}`);
              setAjaxLoading(false);
              break;
          }
        })
        .catch(error => {
          console.error(error);
        });
    },
    [stopStatusPolling]
  );

  const getCsrStatus = useCallback(
    (studyId, requestId) => {
      stopStatusPolling();
      setCurrentTrafficLights([]);
      setCurrentRequestId(requestId);

      CsrAPI.getCsrStatus({ studyId, requestId })
        .then(result => {
          switch (result.status) {
            case "stopped":
              setMainStatus(CSR_STATUS.inProgress);
              break;
            case "ready":
            case "finished":
              setMainStatus(CSR_STATUS.generating);
              statusPollingRef.current = setTimeout(() => {
                getCsrStatus(studyId, requestId);
              }, 3000);
              break;
            case "render available": {
              const formattedDate = FormatDate(result.finished_at);
              setCurrentRequestTime(formattedDate);
              setMainStatus(CSR_STATUS.ready);
              setCurrentTrafficLights(result.message);
              break;
            }
            case "error":
              setMainStatus(CSR_STATUS.errored);
              setErrMsg(CSR_STATUS.errored);
              break;
            default:
              console.warn(`Unexpected CSR status: ${result.status}`);
              break;
          }
        })
        .catch(error => {
          console.error(error);
        });
    },
    [stopStatusPolling]
  );

  const getDocuments = useCallback(
    (studyId, loadingState = "subsequent") => {
      // csr:files:read gates the request itself, not just the list that is
      // rendered from it: without the permission the endpoint is never called,
      // here or from the polling interval below.
      if (!hasPermission(PERMISSIONS.csrFilesRead)) {
        setDocumentsList({ csp: [], sap: [], tfl: [] });
        return;
      }

      if (loadingState === "initial") setAjaxLoading(true);

      CsrAPI.getDocuments({ studyId })
        .then(result => {
          // Any document that isn't PROCESSED means the tables/figures we
          // may already hold are stale: clear them and restart status
          // polling. No-op when everything is processed or a poll cycle is
          // already running.
          dispatch(syncStudyDataWithDocuments(studyId, result));

          let cspArray = [];
          let sapArray = [];
          let tflArray = [];
          let listingArray = [];
          let ciomsArray = [];
          let allFilesStatuses = FILES_STATUS.readyEmpty;

          result?.forEach(doc => {
            if (doc.document_type === DOCUMENT_TYPE.csp) {
              cspArray = [...cspArray, doc];
              listingArray = [...listingArray, doc];
            } else if (doc.document_type === DOCUMENT_TYPE.sap) {
              sapArray = [...sapArray, doc];
              ciomsArray = [...ciomsArray, doc];
            } else if (doc.document_type === DOCUMENT_TYPE.tfl) {
              tflArray = [...tflArray, doc];
            } else if (doc.document_type === PN_DOCUMENT_TYPE.listing) {
              listingArray = [...listingArray, doc];
            } else if (doc.document_type === PN_DOCUMENT_TYPE.cioms) {
              ciomsArray = [...ciomsArray, doc];
            }

            if (doc.status === FILE_UPLOAD_STATUS.errored) {
              allFilesStatuses = FILES_STATUS.warning;
            }

            if (allFilesStatuses !== FILES_STATUS.warning) {
              if (doc.status === FILE_UPLOAD_STATUS.uploaded) {
                allFilesStatuses = FILES_STATUS.pending;
              }

              if (allFilesStatuses !== FILES_STATUS.pending)
                allFilesStatuses = FILES_STATUS.ready;
            }
          });

          setDocumentsList({
            csp: cspArray,
            sap: sapArray,
            tfl: tflArray,
            listing: listingArray,
            cioms: ciomsArray,
          });

          setFilesStatus(allFilesStatuses);
        })
        .catch(() => {
          setFilesStatus(FILES_STATUS.warning);
        })
        .finally(() => {
          setAjaxLoading(false);
        });
    },
    [dispatch, hasPermission]
  );

  const getSafetyDocuments = useCallback(
    (summaryId, loadingState = "subsequent") => {
      // scs:files:read gates the request itself, not just the list rendered
      // from it: without the permission GET .../safety_summary/{id}/files is
      // never called, here or from the polling interval below. The SCS table
      // content the wizard pages map against is pulled from the same pipeline,
      // so it stays empty too.
      if (!hasPermission(PERMISSIONS.scsFilesRead)) {
        setScsDocumentsList({ gids: [], sdap: [], csrs: [], iso: [] });
        return;
      }

      if (loadingState === "initial") setAjaxLoading(true);

      CsrAPI.getSafetySummaryDocuments({ summaryId })
        .then(result => {
          let gidsArray = [];
          let sdapArray = [];
          let csrsArray = [];
          let isoArray = [];
          let allFilesStatuses = FILES_STATUS.readyEmpty;

          result?.forEach(doc => {
            if (doc.document_type === SCS_DOCUMENT_TYPE.gids) {
              gidsArray = [...gidsArray, doc];
            } else if (doc.document_type === SCS_DOCUMENT_TYPE.sdap) {
              sdapArray = [...sdapArray, doc];
            } else if (doc.document_type === SCS_DOCUMENT_TYPE.csrs) {
              csrsArray = [...csrsArray, doc];
            } else if (doc.document_type === SCS_DOCUMENT_TYPE.iso) {
              isoArray = [...isoArray, doc];
            }

            if (doc.status === FILE_UPLOAD_STATUS.errored) {
              allFilesStatuses = FILES_STATUS.warning;
            }

            if (allFilesStatuses !== FILES_STATUS.warning) {
              if (doc.status === FILE_UPLOAD_STATUS.uploaded) {
                allFilesStatuses = FILES_STATUS.pending;
              }

              if (allFilesStatuses !== FILES_STATUS.pending)
                allFilesStatuses = FILES_STATUS.ready;
            }
          });

          setScsDocumentsList({
            gids: gidsArray,
            sdap: sdapArray,
            csrs: csrsArray,
            iso: isoArray,
          });

          setScsFilesStatus(allFilesStatuses);

          // Once every uploaded file is PROCESSED (ready, and not the
          // no-files readyEmpty case) the polling loop stops and the TFL
          // content becomes available — pull it into the dedicated scsData
          // branch of the redux store. The thunk's condition guard dedupes,
          // so repeated ready polls / re-mounts won't re-request it.
          if (allFilesStatuses === FILES_STATUS.ready) {
            dispatch(fetchScsTflContent(summaryId));
          }
        })
        .catch(() => {
          setScsFilesStatus(FILES_STATUS.warning);
        })
        .finally(() => {
          setAjaxLoading(false);
        });
    },
    [dispatch, hasPermission]
  );

  const downloadLatestFile = useCallback(
    (studyId, currentStudyName, fileURL) => {
      setAjaxLoading(true);
      CsrAPI.downloadLatestFile({ studyId, fileURL })
        .then(blob => {
          setErrMsg("");
          saveAs(blob, currentStudyName);
        })
        .catch(error => {
          console.error(error);

          const status =
            error.response?.status || error.originalError?.response?.status;

          if (!error.response && !error.originalError?.response) {
            setErrMsg("No Server Response");
          } else if (status === 401) {
            setErrMsg("Invalid user credentials");
          } else {
            setErrMsg("Failed to download File");
          }
          errRef.current?.focus();
        })
        .finally(() => {
          setAjaxLoading(false);
        });
    },
    []
  );

  useEffect(() => {
    stopStatusPolling();
    if (updateFilesStatus) {
      clearInterval(updateFilesStatus);
      updateFilesStatus = null;
    }
    if (updateScsFilesStatus) {
      clearInterval(updateScsFilesStatus);
      updateScsFilesStatus = null;
    }

    // The permission map decides whether the documents may be fetched at all,
    // so the first load waits for it rather than racing it.
    if (studyId && !permissionsLoading) {
      setErrMsg("");
      setDocumentsList({
        csp: [],
        sap: [],
        tfl: [],
        listing: [],
        cioms: [],
      });
      setScsDocumentsList({
        gids: [],
        sdap: [],
        csrs: [],
        iso: [],
      });

      // LS never used these calls, and PN owns its own documents/data via
      // PatientNarrativeModel, not StudyModel, so only CSR triggers the
      // Study-scoped calls.
      if (app === "scs") {
        // Bind the SCS slice to this summary before any TFL fetch so the
        // slice's summary-scoped guards accept the incoming data.
        dispatch(resetScsForSummary(studyId));
        getSafetyDocuments(studyId, "initial");
      } else if (app === "csr") {
        getStudyInfo(studyId);
        getDocuments(studyId, "initial");
      }
    }

    return () => {
      stopStatusPolling();
      if (updateFilesStatus) {
        clearInterval(updateFilesStatus);
        updateFilesStatus = null;
      }
      if (updateScsFilesStatus) {
        clearInterval(updateScsFilesStatus);
        updateScsFilesStatus = null;
      }
    };
  }, [studyId, stopStatusPolling, permissionsLoading]);

  // Resolve the name shown in the dashboard header for the LS and PN
  // journeys. CSR is filled in by getStudyInfo above (same endpoint, so it
  // isn't requested twice) and SCS reads the scsData slice, but every product
  // clears here first: leaving the previous record's name up while a new one
  // resolves would caption the page with the wrong study.
  useEffect(() => {
    setHeaderStudyName("");

    // No record to name yet on /<product>/new/... or outside a journey, and
    // the reads below are permission-gated, so wait for the map to load.
    if (!studyId || studyId === "new" || permissionsLoading) return undefined;

    let cancelled = false;
    const apply = name => {
      if (!cancelled) setHeaderStudyName(name || "");
    };

    if (app === "pn") {
      CsrAPI.getPatientNarrative({ patientNarrativeId: studyId })
        .then(narrative => apply(narrative?.name))
        .catch(() => {});
    } else if (app === "ls") {
      // There is no GET /lay-summaries/{id}, and CsrAPI.getAllLaySummaries
      // reads a `studies` key that GET /lay-summaries doesn't return — so
      // take the name off /dashboard-documents, which is where the dashboard
      // renders its LS rows from. Rows predating the study_type column have
      // none, and are lay summaries (same assumption Dashboard.js makes).
      CsrAPI.getAllStudies()
        .then(documents =>
          apply(
            documents?.find(
              doc =>
                String(doc.id) === String(studyId) &&
                (doc.study_type || "LS") === "LS"
            )?.name
          )
        )
        .catch(() => {});
    }

    // A failed read just leaves the header without a name — it must never
    // surface an error over a page the user came to do something else on.
    return () => {
      cancelled = true;
    };
  }, [app, studyId, permissionsLoading]);

  useEffect(() => {
    if (studyId) {
      if (filesStatus === FILES_STATUS.pending) {
        updateFilesStatus = setInterval(() => {
          getDocuments(studyId);
        }, 10000);
      } else {
        if (updateFilesStatus) {
          clearInterval(updateFilesStatus);
        }
      }
    }
  }, [filesStatus]);

  useEffect(() => {
    if (studyId) {
      if (scsFilesStatus === FILES_STATUS.pending) {
        updateScsFilesStatus = setInterval(() => {
          getSafetyDocuments(studyId);
        }, 10000);
      } else {
        if (updateScsFilesStatus) {
          clearInterval(updateScsFilesStatus);
          updateScsFilesStatus = null;
        }
      }
    }
  }, [scsFilesStatus]);

  return (
    <CsrContext.Provider
      value={{
        ajaxLoading,
        setAjaxLoading,
        studyInfoLoading,
        errRef,
        errMsg,
        setErrMsg,
        getStudyInfo,
        getLaySummaryInfo,
        getDocuments,
        downloadLatestFile,
        getCsrStatus,
        getLaySummaryStatus,
        stopStatusPolling,
        currentStudyName,
        currentLaySummaryName,
        headerStudyName,
        setCurrentStudyName,
        setCurrentLaySummaryName,
        currentRequestId,
        setCurrentRequestId,
        currentRequestTime,
        setCurrentRequestTime,
        currentTrafficLights,
        setCurrentTrafficLights,
        cspProgressRef,
        sapProgressRef,
        tflProgressRef,
        cspProgress,
        setCspProgress,
        sapProgress,
        setSapProgress,
        tflProgress,
        setTflProgress,
        listingProgressRef,
        ciomsProgressRef,
        listingProgress,
        setListingProgress,
        ciomsProgress,
        setCiomsProgress,
        documentsList,
        setDocumentsList,
        mainStatus,
        setMainStatus,
        filesStatus,
        setFilesStatus,
        questionsStatus,
        setQuestionsStatus,
        currentRequestIdOfLaySummary,
        setCurrentRequestIdOfLaySummary,
        mainStatusOfLaySummary,
        setMainStatusOfLaySummary,
        currentRequestTimeOfLaySummary,
        setCurrentRequestTimeOfLaySummary,
        selectedStudy,
        setSelectedStudy,
        getSafetyDocuments,
        scsDocumentsList,
        setScsDocumentsList,
        scsFilesStatus,
        setScsFilesStatus,
        gidsProgressRef,
        sdapProgressRef,
        csrsProgressRef,
        isoProgressRef,
        gidsProgress,
        setGidsProgress,
        sdapProgress,
        setSdapProgress,
        csrsProgress,
        setCsrsProgress,
        isoProgress,
        setIsoProgress,
      }}>
      <Outlet />
    </CsrContext.Provider>
  );
}
